import './App.scss';

import {IonApp, IonContent, IonPage, setupIonicReact} from '@ionic/react';


/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

import '@aws-amplify/ui-react/styles.css';
/* Theme variables */
import './theme/variables.css';
import Tabs from './Tabs';
import {Authenticator} from "@aws-amplify/ui-react";
import {Amplify} from "aws-amplify";
import {configureAutoTrack} from "aws-amplify/analytics";
import {UserStore} from "./store/UserStore";
import {SignUpFormFields} from "./components/Auth/SignUpFormFields";
import {AuthHeader} from "./components/Auth/AuthHeader";
import React, {useState} from "react";
import {SignOutType} from "./type/SignOutType";
import {AppUpdate} from "@capawesome/capacitor-app-update";
import {flushStores} from "./store/flushStores";
import {APPLE_APP_ID} from "./constants";
import UpdateMessage from "./update-message";

setupIonicReact();

Amplify.configure({
    Auth: {
        Cognito: {
            userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID as string,
            userPoolId: import.meta.env.VITE_USER_POOL_ID as string,
            identityPoolId: import.meta.env.VITE_IDENTITY_POOL_ID as string,
            mfa: {status: "off"},
            signUpVerificationMethod: "code"
        }
    },
    API: {
        REST: {
            "API": {
                endpoint: import.meta.env.VITE_ENDPOINT as string,
                region: import.meta.env.VITE_GATEWAY_REGION as string
            }
        }
    },
    Analytics: {
        Pinpoint: {
            appId: import.meta.env.VITE_PINPOINT_APP_ID as string,
            region: import.meta.env.VITE_PINPOINT_REGION as string
        }
    }
});

configureAutoTrack({
    // REQUIRED, turn on/off the auto tracking
    enable: true,
    // REQUIRED, the event type, it's one of 'event', 'pageView' or 'session'
    type: 'session',
    // OPTIONAL, additional options for the tracked event.
    options: {
        // OPTIONAL, the attributes of the event
        attributes: {
            customizableField: 'attr'
        }
    }
});

configureAutoTrack({
    // REQUIRED, turn on/off the auto tracking
    enable: true,
    // REQUIRED, the event type, it's one of 'event', 'pageView' or 'session'
    type: 'pageView',
    // OPTIONAL, additional options for the tracked event.
    options: {
        // OPTIONAL, the attributes of the event
        attributes: {
            customizableField: 'attr'
        },

        // OPTIONAL, the event name. By default, this is 'pageView'
        eventName: 'pageView',

        // OPTIONAL, the type of app under tracking. By default, this is 'multiPageApp'.
        // You will need to change it to 'singlePage' if your app is a single-page app like React
        appType: 'multiPage',

        // OPTIONAL, provide the URL for the event.
        urlProvider:  () => {
            // the default function
            return window.location.origin + window.location.pathname;
        }
    }
});

configureAutoTrack({
    // REQUIRED, turn on/off the auto tracking
    enable: true,
    // REQUIRED, the event type, it's one of 'event', 'pageView' or 'session'
    type: 'event',
    // OPTIONAL, additional options for the tracked event.
    options: {
        // OPTIONAL, the attributes of the event
        attributes: {
            customizableField: 'attr'
        },
        // OPTIONAL, events you want to track. By default, this is 'click'
        events: ['click'],

        // OPTIONAL, the prefix of the selectors. By default, this is 'data-amplify-analytics-'
        // Per https://www.w3schools.com/tags/att_global_data.asp, always start
        // the prefix with 'data' to avoid collisions with the user agent
        selectorPrefix: 'data-amplify-analytics-'
    }
});

//Amplify.configure(awsExports);


//registerInDevtools({OrganizationStore})

const formFields = {
  signUp: {
      "given_name": {label: 'First Name', isRequired: true},
      "family_name": {label: 'Last Name', isRequired: true},
      "email": { isRequired: true},
      "password": {
          descriptiveText: '8+ characters, at least one upper case, at least one number & at least one special character (e.g. !,#,*)',
          isRequired: true
      }
  }
}

// StatusBar.setOverlaysWebView({ overlay: false });


const App: React.FC = () => {
    const [needsUpdate, setNeedsUpdate] = useState(false);
    const [updateChecked, setUpdateChecked] = useState(false);

    async function checkForUpdates() {
        try {
            const { updateAvailability } = await AppUpdate.getAppUpdateInfo();

            if (updateAvailability === 2) { // 2 means an update is available
                alert("A new version is available. You must update to continue.");

                // Redirect user to App Store or Play Store
                await AppUpdate.openAppStore({appId: APPLE_APP_ID});

                setNeedsUpdate(true);
            }
        } catch (error) {
            console.error("Error checking app update:", error);
        }
    }

    if (!updateChecked) {
        checkForUpdates();
        setUpdateChecked(true);
    }

    return (
        <IonApp className="full-viewport-height">
            { !needsUpdate && (
                <IonPage>
                    <IonContent fullscreen className="ion-padding">

                        <Authenticator
                            loginMechanisms={["email"]}
                            signUpAttributes={['email', "given_name", "family_name"]}
                            formFields={formFields}
                            passwordSettings={{
                                minLength: 8,
                                requireLowercase: true,
                                requireNumbers: true,
                                requireUppercase: true,
                                requireSpecialCharacters: true
                            }}
                            initialState="signIn"
                            components={{
                                Header() {
                                    return (<AuthHeader/>)
                                },
                                SignIn: {},
                                SignUp: {
                                    FormFields() {
                                        return (
                                            <SignUpFormFields/>
                                        );
                                    },
                                },
                            }}
                            services={{
                                async validateCustomSignUp(formData) {
                                    if (!formData.acknowledgement) {
                                        return {
                                            acknowledgement: 'You must agree to the Terms & Conditions',
                                        };
                                    }
                                },
                            }}
                        >
                            {
                                ({signOut, user: _user}) => {
                                    UserStore.update(s => {
                                        s.signOutHook = () => {
                                            flushStores();

                                            const authSignOut = (signOut as SignOutType);
                                            if (authSignOut) {
                                                authSignOut();
                                            }
                                        };
                                    })

                                    return (<Tabs/>)
                                }
                            }
                        </Authenticator>
                    </IonContent>
                </IonPage>
                )}
            <UpdateMessage visible={needsUpdate}/>
        </IonApp>
    );
}

export default App;
